// Form validation utilities for popup forms
import type {
    PopupFormData,
    PopupFormErrors,
    PopupWelcomeContent,
} from "@/shared/types/popup-types";
import { normalizeWhatsAppNumber, whatsAppNumberProblem } from "./phone";

// ============================================
// VALIDATION FUNCTIONS
// ============================================

export function validateEmail(email: string): string | null {
    if (!email.trim()) {
        return "Email is required";
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
        return "Please enter a valid email address";
    }

    return null;
}

export function validateName(name: string): string | null {
    if (!name.trim()) {
        return "Name is required";
    }

    if (name.trim().length < 2) {
        return "Name must be at least 2 characters long";
    }

    return null;
}

export function validateWhatsAppNumber(whatsappNumber: string): string | null {
    if (!whatsappNumber.trim()) {
        return "WhatsApp number is required";
    }

    // Must start with +
    if (!whatsappNumber.startsWith('+')) {
        return "WhatsApp number must start with +";
    }

    // Remove all non-digits for digit-specific validation
    const digitsOnly = whatsappNumber.replace(/\D/g, '');

    // Must have more than 10 digits and less than 20 digits (not counting the +)
    if (digitsOnly.length <= 10) {
        return "WhatsApp number must have more than 10 digits";
    }

    if (digitsOnly.length >= 20) {
        return "WhatsApp number must have less than 20 digits";
    }

    // Judged after the repairs the submit makes (a trunk 0 after +27 is fine).
    return whatsAppNumberProblem(whatsappNumber);
}

// ============================================
// FORM DATA VALIDATION
// ============================================

export function validatePopupForm(
    formData: Partial<PopupFormData>,
    settings: PopupWelcomeContent
): PopupFormErrors {
    const errors: PopupFormErrors = {};

    // Validate name field if enabled and required
    if (settings.name_field.enabled && settings.name_field.required) {
        const nameError = validateName(formData.name || "");
        if (nameError) {
            errors.name = nameError;
        }
    }

    // Validate email field if enabled and required
    if (settings.email_field.enabled && settings.email_field.required) {
        const emailError = validateEmail(formData.email || "");
        if (emailError) {
            errors.email = emailError;
        }
    }

    // Validate WhatsApp field if enabled
    if (settings.whatsapp_field.enabled) {
        const whatsappError = validateWhatsAppNumber(formData.whatsapp_number || "");
        if (whatsappError) {
            errors.whatsapp_number = whatsappError;
        }
    }

    return errors;
}

// ============================================
// FORM DATA SANITIZATION
// ============================================

export function sanitizeFormData(formData: Partial<PopupFormData>): PopupFormData {
    return {
        name: (formData.name || "").trim(),
        email: (formData.email || "").trim().toLowerCase(),
        whatsapp_number: (formData.whatsapp_number || "").trim(),
    };
}

// ============================================
// FORM SUBMISSION VALIDATION
// ============================================

export function validateFormSubmission(
    formData: Partial<PopupFormData>,
    settings: PopupWelcomeContent
): { isValid: boolean; errors: PopupFormErrors; sanitizedData: PopupFormData } {
    const sanitizedData = sanitizeFormData(formData);
    const errors = validatePopupForm(sanitizedData, settings);
    const isValid = Object.keys(errors).length === 0;

    return {
        isValid,
        errors,
        sanitizedData,
    };
}

// ============================================
// FIELD UTILITIES
// ============================================

export function isFieldEnabled(fieldName: keyof PopupWelcomeContent, settings: PopupWelcomeContent): boolean {
    switch (fieldName) {
        case 'name_field':
            return settings.name_field.enabled;
        case 'email_field':
            return settings.email_field.enabled;
        case 'whatsapp_field':
            return settings.whatsapp_field.enabled;
        default:
            return false;
    }
}

export function isFieldRequired(fieldName: keyof PopupWelcomeContent, settings: PopupWelcomeContent): boolean {
    switch (fieldName) {
        case 'name_field':
            return settings.name_field.enabled && settings.name_field.required;
        case 'email_field':
            return settings.email_field.enabled && settings.email_field.required;
        case 'whatsapp_field':
            return settings.whatsapp_field.enabled; // WhatsApp is always required if enabled
        default:
            return false;
    }
}

export function getFieldPlaceholder(fieldName: keyof PopupWelcomeContent, settings: PopupWelcomeContent): string {
    switch (fieldName) {
        case 'name_field':
            return settings.name_field.placeholder;
        case 'email_field':
            return settings.email_field.placeholder;
        case 'whatsapp_field':
            return settings.whatsapp_field.placeholder;
        default:
            return '';
    }
}

// ============================================
// FORMATTING UTILITIES
// ============================================

export function formatWhatsAppNumber(number: string): string {
    // "+27" prefilled + "0715713300" typed = "+27715713300" (see ./phone.ts).
    return normalizeWhatsAppNumber(number);
}

export function displayWhatsAppNumber(number: string): string {
    // Remove all non-digits
    const digitsOnly = number.replace(/\D/g, '');

    if (digitsOnly.length <= 4) {
        return digitsOnly;
    }

    // Format as groups for better readability
    if (digitsOnly.length <= 7) {
        return `${digitsOnly.slice(0, 3)} ${digitsOnly.slice(3)}`;
    }

    if (digitsOnly.length <= 10) {
        return `${digitsOnly.slice(0, 3)} ${digitsOnly.slice(3, 6)} ${digitsOnly.slice(6)}`;
    }

    // For longer numbers, format as country code + number
    return `${digitsOnly.slice(0, -10)} ${digitsOnly.slice(-10, -7)} ${digitsOnly.slice(-7, -4)} ${digitsOnly.slice(-4)}`;
}
